import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ShoppingBag, Boxes, Truck, BarChart3, Sparkles, ShieldCheck,
  Globe2, Cpu, Layers, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReasonLogo, DpiitChip, FlagIN, ReasonFooter, REASON_COMPANY } from "@/components/brand/ReasonLogo";
import { TID } from "@/constants/testIds";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";

const MODULES = [
  { name: "POS · Mobile · Offline POS", icon: ShoppingBag, hint: "Sell anywhere — counter, tablet, or offline." },
  { name: "Inventory · WMS", icon: Boxes, hint: "Real-time stock across stores & warehouses." },
  { name: "Order Management (OMS)", icon: Truck, hint: "Unified omnichannel order orchestration." },
  { name: "CRM · Loyalty · CDP", icon: Sparkles, hint: "One customer view, intelligent loyalty." },
  { name: "Analytics · Executive Command Center", icon: BarChart3, hint: "Real-time KPIs, AI insights, alerts." },
  { name: "DecisionOS™ · Agentic AI", icon: Cpu, hint: "Chat with your business. Take decisions faster." },
  { name: "Finance · ESG · GRC", icon: ShieldCheck, hint: "Compliant, auditable, defensible." },
  { name: "Multi-tenant · Multi-region", icon: Globe2, hint: "SaaS, Private, Hybrid or On-Prem." },
];

const PILLARS = [
  { icon: Layers, t: "Microservice-Ready", d: "Event-driven, API-first, mobile-first, offline-first." },
  { icon: Lock, t: "Strict Tenant Isolation", d: "Audit, soft-delete, versioning, full data lineage." },
  { icon: Cpu, t: "AI-First", d: "Bring your own keys: OpenAI · Anthropic · Gemini · Ollama." },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto h-16 px-4 sm:px-6 lg:px-8 flex items-center">
          <Link to="/"><ReasonLogo /></Link>
          <nav className="ml-10 hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            <a href="#modules" className="hover:text-foreground">Platform</a>
            <a href="#pillars" className="hover:text-foreground">Architecture</a>
            <a href="#ai" className="hover:text-foreground">DecisionOS™</a>
            <a href="#trust" className="hover:text-foreground">Trust</a>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitcher />
            <Button data-testid={TID.navLogin} variant="ghost" onClick={() => nav("/login")}>Sign in</Button>
            <Button data-testid={TID.navRegister} className="bg-primary hover:bg-primary/90" onClick={() => nav("/register")}>
              Start free <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="flex items-center gap-3 animate-fade-up">
            <DpiitChip />
            <span className="text-xs text-muted-foreground hidden sm:inline">v1.0 · Made for global Indian retail</span>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl animate-fade-up">
            One operating system for the<br />
            <span className="text-primary">entire retail business.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed animate-fade-up">
            REASON RetailOS™ unifies POS, Inventory, OMS, WMS, Merchandising, CRM, Loyalty,
            Finance, Workforce, ESG, GRC, Analytics and Agentic AI into a single, auditable,
            multi-tenant platform. Built for SaaS, Private, Hybrid or On-Premise — and yours
            to extend.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 animate-fade-up">
            <Button data-testid={TID.heroGetStarted} size="lg" className="bg-primary hover:bg-primary/90" onClick={() => nav("/register")}>
              Get started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button data-testid={TID.heroSeePos} size="lg" variant="outline" onClick={() => nav("/login")}>
              See it in action
            </Button>
          </div>

          {/* Trust strip */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 text-xs text-muted-foreground border-t border-border pt-8 max-w-3xl">
            <Stat n="20+" l="Integrated modules" />
            <Stat n="4" l="Deployment modes" />
            <Stat n="3" l="LLM providers, BYO keys" />
            <Stat n="100%" l="Tenant-isolated" />
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">The platform</p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-2">
                Every system your retail business needs — under one roof.
              </h2>
            </div>
            <p className="max-w-md text-sm text-muted-foreground">
              Phase 1 ships POS, Inventory, OMS, ECC and DecisionOS — the rest are next on the roadmap, all backward-compatible.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map((m) => (
              <div key={m.name} className="rounded-md border border-border bg-card p-5 hover:border-primary/40 hover:shadow-sm transition-all">
                <m.icon className="h-5 w-5 text-primary" />
                <div className="mt-3 text-sm font-semibold">{m.name}</div>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{m.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section id="pillars" className="border-b border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Architecture principles</p>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-2 max-w-3xl">
            Microservice-ready, event-driven, API-first, mobile-first, offline-first, AI-first, security-first.
          </h2>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {PILLARS.map((p) => (
              <div key={p.t} className="rounded-md border border-border bg-card p-6">
                <p.icon className="h-5 w-5 text-primary" />
                <div className="mt-3 text-sm font-semibold">{p.t}</div>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI section */}
      <section id="ai" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">DecisionOS™</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-2">
              Chat with your retail business.
            </h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Plug in your own OpenAI, Anthropic or Gemini keys. Super admins configure providers
              per tenant from the dashboard — no platform-supplied keys, no vendor lock-in.
              Your data, your models, your control.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li>· Bring your own API keys</li>
              <li>· Choose model per question</li>
              <li>· Encrypted at rest, tenant-scoped</li>
              <li>· Full audit trail of every prompt</li>
            </ul>
          </div>
          <div className="rounded-md border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-success" />
              <span>DecisionOS · provider: Claude Sonnet 4.5</span>
            </div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="rounded-md border border-border p-3 bg-secondary/50">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">You</span>
                <p>Why did Mumbai Flagship revenue drop 18% this week?</p>
              </div>
              <div className="rounded-md border border-border p-3">
                <span className="text-[10px] uppercase tracking-widest text-primary">DecisionOS</span>
                <p className="text-foreground/90">
                  Footfall dropped 22% (rain warning Jun 8-10). However, conversion held at 31%
                  and AOV grew +6%. Footwear sold out on Saturday — recommend rerouting 40
                  units from Pune DC.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA + Footer */}
      <section id="trust" className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <FlagIN className="!h-4 !w-6" />
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-3">
              Built in India. Ready for the world.
            </h2>
            <p className="mt-3 text-sm text-primary-foreground/80 max-w-lg">
              REASON RetailOS™ is DPIIT-recognised, designed for the demands of Indian retail —
              GST, regional languages, offline mode, UPI — and equally at home in flagship malls
              from Bengaluru to Bahrain.
            </p>
          </div>
          <div className="flex md:justify-end gap-3">
            <Button size="lg" variant="secondary" onClick={() => nav("/register")}>Start free trial</Button>
            <Button size="lg" variant="outline" className="bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10" onClick={() => nav("/login")}>Sign in</Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-3 gap-6 text-xs text-muted-foreground">
          <div>
            <ReasonLogo size={24} />
            <p className="mt-3 leading-relaxed max-w-xs">
              {REASON_COMPANY.product} is built by {REASON_COMPANY.legalName} — a DPIIT-recognised
              Indian enterprise software company.
            </p>
          </div>
          <div>
            <p className="text-foreground font-semibold">Company</p>
            <ul className="mt-2 space-y-1.5">
              <li>{REASON_COMPANY.legalName}</li>
              <li><a href={REASON_COMPANY.website} target="_blank" rel="noreferrer" className="hover:text-foreground">{REASON_COMPANY.websiteShort}</a></li>
              <li><a href={`mailto:${REASON_COMPANY.contact}`} className="hover:text-foreground">{REASON_COMPANY.contact}</a></li>
              <li>India · DPIIT Registered</li>
            </ul>
          </div>
          <div className="md:text-right space-y-2">
            <DpiitChip />
            <p>© {new Date().getFullYear()} {REASON_COMPANY.product}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-2xl font-semibold text-foreground">{n}</div>
      <div className="mt-0.5">{l}</div>
    </div>
  );
}
